// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions from "@adl/App/Components/Product/ProductActions";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    showCompare?: boolean;
    showAddToCart?: boolean;
    isGridPage?: boolean;
    showImageBadge?: boolean;
    showTextBadge?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // Xng change
    compareAddToListWrapper?: InjectableCss;
    gridPrice?: ProductPriceStyles;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            ${({ isGridPage }: any) => isGridPage && ` &&& { margin-bottom: 0; } `}
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            ${({ isGridPage }: any) =>
                isGridPage
                    ? `
                margin-top: 0;
            `
                    : `
                margin-top: 16px;
            `}
        `,
    },
    addToCartButton: {
        css: css`
            flex-grow: 1;
            margin-left: 12px;
        `,
    },
    price: {
        wrapper: {
            css: css`
                text-align: right;
                > div {
                    justify-content: end;
                }
            `,
        },
        price: {
            priceText: {
                size: 18,
                weight: "bold",
                css: css`
                    line-height: 22px;
                `,
            },
        },
    },
    quantityOrdered: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 100px;
            `,
        },
    },

    compareAddToListWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            div[data-test-selector="productList_CompareCheckbox"] {
                padding-top: 0;
            }
            ${({ isGridPage }: any) => (!isGridPage ? `margin-top: 22px;` : "")}
        `,
    },
    gridPrice: {
        wrapper: {
            css: css`
                text-align: left;
            `,
        },
        price: {
            priceText: {
                size: 18,
                weight: "bold",
                css: css`
                    line-height: 22px;
                `,
            },
        },
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    showCompare,
    showAddToCart,
    isGridPage,
    showImageBadge,
    showTextBadge,
}) => {
    return (
        <StyledWrapper {...styles.wrapper} {...{ isGridPage }}>
            {showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={isGridPage ? styles.gridPrice : styles.price}
                        /*  XNGAGE Changes */
                        {...{ showDiscountPrice: true, showRetailPrice: true }}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {showAddToCart && (
                <StyledWrapper {...styles.addToCartWrapper} {...{ isGridPage }}>
                    <ProductActions shouldWaitInventory={true} />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.compareAddToListWrapper} {...{ isGridPage }}>
                <StyledWrapper>
                    {showCompare && (
                        <ProductListProductImage
                            showImage={false}
                            showCompare={true}
                            showImageBadge={showImageBadge}
                            showTextBadge={showTextBadge}
                        />
                    )}
                </StyledWrapper>

                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

ProductListActions.defaultProps = {
    showAddToCart: true,
    isGridPage: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
